<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <card :title="$t('cars.customFields')" :on-add-object="openCreate">
          <p class="text-sm text-secondary">{{ $t('cars.customFieldsDescription') }}</p>
          <div v-if="!loaded" class="text-center text-secondary py-4">{{ $t('common.loading') }}</div>
          <div v-else-if="fields.length === 0" class="text-center text-secondary py-4">
            {{ $t('cars.noCustomFields') }}
          </div>
          <el-table v-else :data="fields" row-key="id" style="width:100%">
            <el-table-column :label="$t('cars.customFieldName')" prop="name" min-width="160" />
            <el-table-column :label="$t('cars.customFieldType')" min-width="120">
              <template #default="scope">{{ $t(typeLabelKey(scope.row.fieldType)) }}</template>
            </el-table-column>
            <el-table-column label="" width="140" align="right">
              <template #default="scope">
                <el-button size="small" @click="openEdit(scope.row)">
                  <i class="material-icons" style="font-size:1rem">edit</i>
                </el-button>
                <el-button size="small" type="danger" @click="confirmDelete(scope.row)">
                  <i class="material-icons" style="font-size:1rem">delete</i>
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </card>
      </div>
    </div>

    <!-- Create / edit dialog -->
    <el-dialog v-model="showForm" :title="editMode ? $t('cars.editCustomField') : $t('cars.newCustomField')" width="480">
      <div class="mb-3">
        <label class="form-label">{{ $t('cars.customFieldName') }} <span class="text-danger">*</span></label>
        <el-input v-model="form.name" maxlength="100" />
      </div>
      <div class="mb-3">
        <label class="form-label">{{ $t('cars.customFieldType') }}</label>
        <el-select v-model="form.fieldType" class="w-100" :disabled="editMode">
          <el-option v-for="option in fieldTypes" :key="option.value" :value="option.value" :label="$t(option.labelKey)" />
        </el-select>
        <small v-if="editMode" class="text-secondary">{{ $t('cars.customFieldTypeLocked') }}</small>
      </div>
      <template #footer>
        <el-button @click="showForm = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">{{ $t('common.submit') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import api from '../../services/api';
import Card from '../../components/Card.vue';
import { ElDialog, ElInput, ElButton, ElTable, ElTableColumn, ElSelect, ElOption, ElMessageBox } from 'element-plus';
import { CAR_TOOL_GROUP_TYPE_ID, CUSTOM_FIELD_TYPES, CUSTOM_FIELD_TYPE, labelKeyFor } from './carHelper';

export default {
  name: 'ManageCarCustomFields',
  components: {
    Card,
    ElDialog, ElInput, ElButton, ElTable, ElTableColumn, ElSelect, ElOption,
  },
  data() {
    return {
      loaded: false,
      fields: [],
      fieldTypes: CUSTOM_FIELD_TYPES,
      showForm: false,
      saving: false,
      editMode: false,
      form: { id: null, name: '', fieldType: CUSTOM_FIELD_TYPE.TEXT },
    };
  },
  created() {
    this.fetchFields();
  },
  methods: {
    typeLabelKey(fieldType) {
      return labelKeyFor(CUSTOM_FIELD_TYPES, fieldType);
    },
    async fetchFields() {
      this.loaded = false;
      try {
        const res = await api.get(`/ToolGroupCustomField?toolGroupTypeId=${CAR_TOOL_GROUP_TYPE_ID}`);
        this.fields = res.data;
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0 });
      } finally {
        this.loaded = true;
      }
    },
    openCreate() {
      this.editMode = false;
      this.form = { id: null, name: '', fieldType: CUSTOM_FIELD_TYPE.TEXT };
      this.showForm = true;
    },
    openEdit(row) {
      this.editMode = true;
      this.form = { id: row.id, name: row.name, fieldType: row.fieldType };
      this.showForm = true;
    },
    async submitForm() {
      if (!this.form.name || !this.form.name.trim()) {
        this.$toast.error(this.$t('cars.customFieldNameRequired'), { timeout: 3000 });
        return;
      }
      this.saving = true;
      try {
        if (this.editMode) {
          await api.put(`/ToolGroupCustomField/${this.form.id}`, { name: this.form.name.trim() });
        } else {
          await api.post('/ToolGroupCustomField', {
            toolGroupTypeId: CAR_TOOL_GROUP_TYPE_ID,
            name: this.form.name.trim(),
            fieldType: this.form.fieldType,
          });
        }
        this.$toast.success(this.$t('common.saved'), { timeout: 2000 });
        this.showForm = false;
        await this.fetchFields();
      } catch (err) {
        const alreadyExists = Array.isArray(err?.response?.data) && err.response.data.some(e => e.identifier === 'Already Exists');
        this.$toast.error(alreadyExists ? this.$t('cars.customFieldExists') : this.$t('common.unableToUpdate'), { timeout: 0 });
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete(row) {
      try {
        await ElMessageBox.confirm(
          this.$t('cars.customFieldDeleteConfirm', { name: row.name }),
          this.$t('common.confirm'),
          { type: 'warning' }
        );
      } catch { return; }
      try {
        await api.delete(`/ToolGroupCustomField/${row.id}`);
        this.$toast.success(this.$t('common.deleted'), { timeout: 2000 });
        await this.fetchFields();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToDelete'), { timeout: 0 });
      }
    },
  },
};
</script>
